import { AlertTriangle, CheckCircle2, Printer, RefreshCw, TestTube2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  printerReceiptPreviewCatalog,
  type PrinterReceiptBlock,
  type PrinterReceiptDocument,
} from '@ffs-studio/protocol/printer-receipt';
import {
  changedPrinterPaperRoll,
  loadPrinter,
  printPrinterTest,
  printPrinterTestEvent,
  reprintLastPrinterEvent,
  setPrinterMasterEnabled,
  setPrinterTargetAgent,
  type PrinterSnapshot,
  type PrinterTestEventKind,
} from './printer-api';
import './printer.css';

function when(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toLocaleString('en-GB') : '—';
}

export function PrinterPage({ role }: { role: 'owner' | 'operator' }) {
  const [snapshot, setSnapshot] = useState<PrinterSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const catalog = useMemo(() => printerReceiptPreviewCatalog(), []);
  const [previewId, setPreviewId] = useState(catalog[0]?.id ?? '');
  const [testEventKind, setTestEventKind] =
    useState<PrinterTestEventKind>('follow');
  const preview = catalog.find((item) => item.id === previewId) ?? catalog[0] ?? null;

  const refresh = useCallback(async () => {
    try {
      setSnapshot(await loadPrinter());
      setMessage('');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Printer status could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => void refresh(), 5000);
    return () => window.clearInterval(timer);
  }, [refresh]);

  async function mutate(operation: () => Promise<unknown>, success: string) {
    setBusy(true);
    setMessage('');
    try {
      await operation();
      await refresh();
      setMessage(success);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Printer action failed.');
    } finally {
      setBusy(false);
    }
  }

  async function toggleMaster() {
    if (!snapshot) return;
    const enabling = !snapshot.masterEnabled;
    if (!window.confirm(enabling ? 'Turn physical receipt printing ON?' : 'Turn physical receipt printing OFF? Queued physical jobs will be cancelled.')) return;
    await mutate(() => setPrinterMasterEnabled(enabling), `Physical printing ${enabling ? 'enabled' : 'disabled'}.`);
  }

  async function changeTarget(agentId: string) {
    await mutate(() => setPrinterTargetAgent(agentId || null), agentId ? 'Printer Agent updated.' : 'Printer Agent cleared.');
  }

  async function testPrint() {
    if (!snapshot) return;
    if (!window.confirm('Print one bounded plain-text FFSControl printer test?\n\nThis tests the basic spooler path and does not create Twitch history.')) return;
    await mutate(printPrinterTest, 'Basic printer test accepted.');
  }

  async function testEvent() {
    if (!snapshot) return;
    if (!window.confirm(`Print one safe ${testEventKind} event receipt?\n\nThis uses fixed test data and does not create Twitch event history.`)) return;
    await mutate(
      () => printPrinterTestEvent(testEventKind),
      `Safe ${testEventKind} test receipt accepted.`,
    );
  }

  async function reprintLast() {
    if (!snapshot?.lastReprintableEvent) return;
    const event = snapshot.lastReprintableEvent;
    if (!window.confirm(`Reprint the last genuine event receipt?\n\n${event.displayName} · ${event.eventType}\n${when(event.occurredAt)}\n\nThis creates a new printer job but does not duplicate the Twitch event.`)) return;
    await mutate(reprintLastPrinterEvent, 'Last genuine event receipt reprint accepted.');
  }

  async function changedRoll() {
    if (!snapshot) return;
    if (!window.confirm('Confirm that you have physically fitted a NEW PAPER ROLL.\n\nFFSControl will reset the durable receipt-chain position, print a small ready slip, cut it, and leave the next FFSControl masthead pre-printed for the next genuine receipt.')) return;
    await mutate(changedPrinterPaperRoll, 'New paper roll reset and priming accepted.');
  }

  if (loading && !snapshot) return <div className="ffs-next-loading">Loading Printer…</div>;

  const ready = snapshot?.hardware.status === 'ready' && snapshot.transportAvailable;
  const canPrint = snapshot?.masterEnabled === true && ready;
  const canPrime = canPrint && snapshot?.chainResetAvailable === true;

  return (
    <div className="ffs-workspace-page ffs-printer-page">
      {message ? <div className="ffs-workspace-message">{message}</div> : null}
      <section className="ffs-workspace-hero">
        <div><small>STREAM RECEIPT PRINTER</small><h2>Printer</h2><p>Keep everyday status and test printing obvious; hardware selection and master gating stay deliberate.</p></div>
        <div className="ffs-printer-hero-actions"><span className={`ffs-status ${canPrint ? 'good' : snapshot?.masterEnabled ? 'warning' : ''}`}>{canPrint ? 'READY TO PRINT' : snapshot?.masterEnabled ? 'PRINTING BLOCKED' : 'MASTER OFF'}</span><button type="button" className="ffs-button secondary" disabled={busy} onClick={() => void refresh()}><RefreshCw size={15} /> Refresh</button></div>
      </section>

      <section className="ffs-printer-status-grid">
        <StatusCard label="Master printing" value={snapshot?.masterEnabled ? 'ON' : 'OFF'} detail={snapshot?.masterEnabled ? 'Twitch events may dispatch while hardware is ready.' : 'No physical receipt jobs can dispatch.'} good={snapshot?.masterEnabled === true} />
        <StatusCard label="Printer hardware" value={(snapshot?.hardware.status ?? 'unknown').toUpperCase()} detail={snapshot?.hardware.queueName ?? 'No verified queue'} good={ready} />
        <StatusCard label="Printer Agent" value={snapshot?.targetAgent?.name ?? 'Not selected'} detail={snapshot?.targetAgent?.connected ? 'Connected' : 'Offline / unavailable'} good={snapshot?.targetAgent?.connected === true} />
        <StatusCard label="Last status check" value={when(snapshot?.hardware.lastSuccessfulStatusCheck)} detail={snapshot?.hardware.interfaceSummary ?? snapshot?.hardware.detail ?? 'No hardware detail'} good={ready} />
      </section>

      <section className="ffs-printer-main-grid">
        <article className="ffs-workspace-card ffs-printer-control-card">
          <header><div><small>PHYSICAL OUTPUT</small><h3>Epson TM‑T88IV</h3></div><Printer size={19} /></header>
          <div className={`ffs-printer-health ${ready ? 'ready' : 'blocked'}`}>
            {ready ? <CheckCircle2 size={21} /> : <AlertTriangle size={21} />}
            <div><strong>{ready ? 'Hardware checks passed' : 'Printing is not currently available'}</strong><span>{snapshot?.hardware.detail ?? 'Printer status unavailable.'}</span></div>
          </div>
          <div className="ffs-printer-facts">
            <Fact label="Queue" value={snapshot?.hardware.queueName ?? '—'} />
            <Fact label="Driver" value={snapshot?.hardware.driverName ?? '—'} />
            <Fact label="Interface" value={snapshot?.hardware.interfaceSummary ?? '—'} />
          </div>
          {snapshot?.hardware.lastError ? <div className="ffs-printer-warning">{snapshot.hardware.lastError}</div> : null}
          <div className="ffs-printer-operations">
            <div className="ffs-printer-operation">
              <div><strong>Safe event test</strong><span>Real receipt renderer · fixed synthetic data · no Twitch history.</span></div>
              <div className="ffs-printer-event-test">
                <select value={testEventKind} disabled={busy || !canPrint} onChange={(event) => setTestEventKind(event.target.value as PrinterTestEventKind)}>
                  <option value="follow">Follow</option>
                  <option value="subscription">Subscription</option>
                  <option value="resubscription">Resubscription</option>
                  <option value="bits">Bits / Cheer</option>
                  <option value="raid">Raid</option>
                  <option value="gift-subs">Gift Subs</option>
                </select>
                <button type="button" className="ffs-button primary" disabled={busy || !canPrint} onClick={() => void testEvent()}><TestTube2 size={15} /> Print Event Test</button>
              </div>
            </div>
            <div className="ffs-printer-operation">
              <div><strong>Reprint last genuine event</strong><span>{snapshot?.lastReprintableEvent ? `${snapshot.lastReprintableEvent.displayName} · ${when(snapshot.lastReprintableEvent.occurredAt)}` : 'No completed Twitch event receipt is available.'}</span></div>
              <button type="button" className="ffs-button secondary" disabled={busy || !canPrint || !snapshot?.lastReprintableEvent} onClick={() => void reprintLast()}>Reprint Last Event</button>
            </div>
            <div className="ffs-printer-operation paper-roll">
              <div><strong>Changed Roll / New Paper Roll</strong><span>{snapshot?.chainResetAvailable ? 'Resets the durable chain and primes the next FFSControl masthead.' : 'Requires the updated Home Bot printer Agent.'}</span></div>
              <button type="button" className="ffs-button warning" disabled={busy || !canPrime} onClick={() => void changedRoll()}>Changed Roll</button>
            </div>
          </div>
          <div className="ffs-printer-operation basic-test"><div><strong>Basic spooler test</strong><span>Uses fixed server-owned test content. This can be blocked while the receipt chain is already primed.</span></div><button type="button" className="ffs-button secondary" disabled={busy || !canPrint} onClick={() => void testPrint()}>Print Test Receipt</button></div>
        </article>

        <article className="ffs-workspace-card ffs-printer-settings-card">
          <header><div><small>STREAM CONTROLS</small><h3>Dispatch gate</h3></div><span>{role === 'owner' ? 'Owner' : 'Lead Moderator'}</span></header>
          <div className="ffs-printer-setting-block"><div><strong>Master printing</strong><span>This is the physical dispatch kill switch. Turning it off also cancels physical jobs still waiting for Agent dispatch.</span></div><button type="button" className={`ffs-button ${snapshot?.masterEnabled ? 'warning' : 'primary'}`} disabled={busy} onClick={() => void toggleMaster()}>{snapshot?.masterEnabled ? 'Turn Printing Off' : 'Turn Printing On'}</button></div>
          <label className="ffs-printer-agent-field"><span>Target printer Agent</span><select value={snapshot?.targetAgentId ?? ''} disabled={busy} onChange={(event) => void changeTarget(event.target.value)}><option value="">No printer Agent</option>{(snapshot?.availableAgents ?? []).map((agent) => <option key={agent.id} value={agent.id}>{agent.name} · {agent.connected ? 'connected' : 'offline'}{agent.printerCapability ? ' · printer capable' : ''}</option>)}</select></label>
          <div className="ffs-printer-audit"><span>Last changed</span><strong>{when(snapshot?.updatedAt)}</strong><small>{snapshot?.updatedBy ? `by ${snapshot.updatedBy}` : 'No recorded operator'}</small></div>
        </article>
      </section>

      <section className="ffs-workspace-card ffs-printer-preview-card">
        <header><div><small>VISUAL CHECK</small><h3>Receipt layout preview</h3></div><span>Preview only · cannot print</span></header>
        <div className="ffs-printer-preview-toolbar"><label><span>Sample receipt</span><select value={preview?.id ?? ''} onChange={(event) => setPreviewId(event.target.value)}>{catalog.map((item) => <option value={item.id} key={item.id}>{item.label}</option>)}</select></label><p>The preview uses the same semantic receipt composer, but deliberately does not fetch Twitch images or send anything to the printer.</p></div>
        <div className="ffs-printer-preview-stage">{preview ? <ReceiptPaper document={preview.document} /> : <div className="ffs-empty-state">No receipt samples are available.</div>}</div>
      </section>
    </div>
  );
}

function StatusCard({ label, value, detail, good }: { label: string; value: string; detail: string; good: boolean }) {
  return <article className={`ffs-printer-status ${good ? 'good' : ''}`}><span>{label}</span><strong>{value}</strong><small>{detail}</small></article>;
}

function Fact({ label, value }: { label: string; value: string }) {
  return <div><span>{label}</span><strong>{value}</strong></div>;
}

function ReceiptPaper({ document }: { document: PrinterReceiptDocument }) {
  return <div className="ffs-printer-paper">{document.blocks.map((block, index) => renderBlock(block, index))}</div>;
}

function renderBlock(block: PrinterReceiptBlock, index: number) {
  if (block.type === 'wordmark') return <div className="receipt-wordmark" key={index}><strong>FFS</strong><span>Control</span></div>;
  if (block.type === 'separator') return <div className="receipt-separator" key={index} />;
  if (block.type === 'text') return <div className="receipt-text" data-align={block.alignment} data-size={block.size} data-emphasis={block.emphasis} key={index}>{block.text}</div>;
  if (block.type === 'profile-image') return <ProfilePlaceholder name={block.displayName} large={block.scale === 'full-width'} key={index} />;
  if (block.type === 'profile-grid') return <div className="receipt-profile-grid" key={index}>{block.items.map((item) => <div key={item.twitchUserId}><ProfilePlaceholder name={item.displayName} large={false} /><strong>{item.displayName}</strong></div>)}</div>;
  if (block.type === 'name-list') return <div className="receipt-name-list" key={index}>{block.names.map((name, nameIndex) => <span key={`${name}-${nameIndex}`}>{name}</span>)}</div>;
  return <div className="receipt-time" key={index}>{when(block.iso)}</div>;
}

function ProfilePlaceholder({ name, large }: { name: string; large: boolean }) {
  const initials = name.trim().split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase() ?? '').join('') || '?';
  return <div className={`receipt-profile ${large ? 'large' : ''}`}><span>{initials}</span><small>{name}</small></div>;
}
